// commentsService.ts
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  DocumentData,
  QuerySnapshot,
} from "firebase/firestore";
import {
  AddCommentRequestType,
  CommentType,
  FlagCommentAsInappropriateRequestType,
} from "../../models/commentType";
import { auth, firestore, functionsBaseUrl, logEvent } from "../../firebase";
import { MetricsNames } from "../firebase/metricsService";

export const CommentsClient = {
  /**
   * Fetch all comments for a restaurant from the flat comments collection,
   * group into top‐level + replies by ancestors[], and compute avg rating & count.
   */
  getComments: async (restaurantId: string) => {
    // 1) Load every comment
    const commentsCol = collection(
      firestore,
      "restaurant-comments-collection",
      restaurantId,
      "comments"
    );
    const allSnap = await getDocs(
      query(
        commentsCol,
        where("isActive", "==", true),
        orderBy("date", "asc")
      )
    );
    const allComments = allSnap.docs.map(d => d.data() as CommentType);

    // 2) Compute rating stats for top-level only
    const topLevel = allComments.filter(c => c.ancestors.length === 0);
    const numRatings = topLevel.length;
    const rating =
      numRatings > 0
        ? topLevel.reduce((sum, c) => sum + (c.rating || 0), 0) / numRatings
        : 0;

    // 3) Build a map of id → node (with empty replies)
    const nodeMap = new Map<string, CommentType & { replies: CommentType[] }>();
    allComments.forEach(c => {
      nodeMap.set(c.id, {
        ...c,
        text: c.isInappropriate ? "[DELETED]" : c.text,
        replies: []
      });
    });

    // 4) Attach every non-root to its immediate parent
    //    Sort ascending so parents always exist before children
    allComments
      .sort((a, b) => a.ancestors.length - b.ancestors.length)
      .forEach(c => {
        if (c.ancestors.length > 0) {
          const parentId = c.ancestors[c.ancestors.length - 1];
          const parentNode = nodeMap.get(parentId);
          const childNode = nodeMap.get(c.id);
          if (parentNode && childNode) {
            parentNode.replies.push(childNode);
          }
        }
      });

    // 5) Collect only the roots (ancestors.length === 0)
    const comments = Array.from(nodeMap.values()).filter(
      c => c.ancestors.length === 0
    );

    return { comments, rating, numRatings };
  },

  /**
   * Add a new comment or reply via your HTTP Cloud Function.
   */
  addComment: async ({
    restaurantId,
    authorId,
    author,
    text,
    rating,
    parentId,
  }: {
    restaurantId: string;
    authorId: string;
    author: string;
    text: string;
    rating: number;
    parentId?: string;
  }): Promise<string> => {
    if (!auth.currentUser) throw new Error("Unauthorized");
    const idToken = await auth.currentUser.getIdToken();

    const payload: AddCommentRequestType = {
      restaurantId,
      author,
      text,
      rating,
    };
    if (parentId) payload.parentId = parentId;
    console.log("payload", payload, "parentId", parentId)

    const res = await fetch(`${functionsBaseUrl}/addComment`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json();
      logEvent(MetricsNames.FAILURE_POSTING_COMMENT);
      throw new Error(err.error || "Failed to add comment");
    }
    const { newId } = await res.json();
    return newId as string;
  },

  /**
   * Flag a comment (or reply) as inappropriate via HTTP Cloud Function.
   */
  flagCommentAsInappropriate: async ({
    restaurantId,
    commentId,
  }: {
    restaurantId: string;
    commentId: string;
  }): Promise<void> => {
    if (!auth.currentUser) throw new Error("Unauthorized");
    const idToken = await auth.currentUser.getIdToken();

    const payload: FlagCommentAsInappropriateRequestType = {
      restaurantId,
      commentId,
    };

    const res = await fetch(
      `${functionsBaseUrl}/flagCommentAsInappropriate`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to flag comment");
    }
  },
};
